import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useNotificationUnreadCountsQuery } from '@/services/api/notificationsService';

import { CaseDetailsPanel } from './components/CaseDetailsPanel';
import { NotificationDetail } from './components/NotificationDetail';
import { NotificationList } from './components/NotificationList';
import { NotificationNav, type NotificationNavTab } from './components/NotificationNav';

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize state from URL params
  const initialTab = (searchParams.get('tab') as NotificationNavTab) || 'mentions';
  const initialNotificationId = searchParams.get('notification_id');

  const [activeTab, setActiveTab] = useState<NotificationNavTab>(initialTab);
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(
    initialNotificationId,
  );

  // Sync state FROM URL when URL params change (e.g., from deep link navigation)
  useEffect(() => {
    const urlTab = searchParams.get('tab') as NotificationNavTab | null;
    const urlNotificationId = searchParams.get('notification_id');

    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    if (urlNotificationId !== selectedNotificationId) {
      setSelectedNotificationId(urlNotificationId);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch unread counts with 1min polling
  const { data: unreadCountsData } = useNotificationUnreadCountsQuery(undefined, {
    pollingInterval: 60000,
  });

  const unreadCounts = unreadCountsData || { total: 0, messages: 0, mentions: 0, updates: 0 };

  // Sync state TO URL
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('tab', activeTab);
    if (selectedNotificationId) {
      params.set('notification_id', selectedNotificationId);
    }
    navigate(`?${params.toString()}`, { replace: true });
  }, [activeTab, selectedNotificationId, navigate]);

  const handleTabChange = (tab: NotificationNavTab) => {
    setActiveTab(tab);
    setSelectedNotificationId(null); // Clear selection when changing tabs
  };

  const handleSelectNotification = (id: string) => {
    setSelectedNotificationId(id);
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] bg-gray-50 flex">
      <div className="w-full flex">
        {/* Left Nav - 56px fixed */}
        <NotificationNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          unreadCounts={unreadCounts}
        />

        {/* Resizable panels for list + detail only */}
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          {/* Notification List */}
          <ResizablePanel
            defaultSize={25}
            minSize={15}
            maxSize={35}
            className="min-w-[240px]"
          >
            <NotificationList
              tab={activeTab}
              selectedId={selectedNotificationId}
              onSelect={handleSelectNotification}
            />
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-gray-200 hover:bg-gray-300" />

          {/* Detail Content - takes remaining space */}
          <ResizablePanel defaultSize={78} minSize={50}>
            <NotificationDetail notificationId={selectedNotificationId} tab={activeTab} />
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* Case Details - fixed width, always reserve space for consistency */}
        <div className="hidden xl:block w-72 flex-shrink-0">
          {selectedNotificationId && (
            <CaseDetailsPanel selectedId={selectedNotificationId} tab={activeTab} />
          )}
        </div>
      </div>
    </div>
  );
};
